import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { Dispatch } from 'redux';
import { Employee, Position, Tag } from '@synergy/models';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

import { Api } from '../../../../../../api';
import { AppState } from '../../../../../../store/models/app-state';

export const CUSTOMER_EMPLOYEES_ADD = 'CUSTOMER_EMPLOYEES_ADD';
export const CUSTOMER_EMPLOYEES_REMOVE = 'CUSTOMER_EMPLOYEES_REMOVE';
export const CUSTOMER_EMPLOYEES_SET_AS_MAIN_CONTACT = 'CUSTOMER_EMPLOYEES_SET_AS_MAIN_CONTACT';
export const CUSTOMER_EMPLOYEES_SET_AS_BONUS_CONTACT = 'CUSTOMER_EMPLOYEES_SET_AS_BONUS_CONTACT';
export const CUSTOMER_EMPLOYEES_CHANGE_POSITION = 'CUSTOMER_EMPLOYEES_CHANGE_POSITION';
export const CUSTOMER_EMPLOYEES_CHANGE_TAGS = 'CUSTOMER_EMPLOYEES_CHANGE_TAGS';
export const CUSTOMER_EMPLOYEES_REQUEST = 'CUSTOMER_EMPLOYEES_REQUEST';
export const CUSTOMER_EMPLOYEES_RECEIVED = 'CUSTOMER_EMPLOYEES_RECEIVED';

const receiveCustomerEmployees = (employees: Employee[], bonusContactCount: number) => {
    return {
        type: CUSTOMER_EMPLOYEES_RECEIVED,
        employees,
        bonusContactCount,
    };
};

export const fetchCustomerEmployees = (customerId: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CUSTOMER_EMPLOYEES_REQUEST, customerId });
        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.Employees.getEmployees(customerId);
        if (response.success) {
            dispatch(receiveCustomerEmployees(response.result.employees, response.result.bonusContactCount));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const addEmployee = (customerId: number, employee: Partial<Employee>) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CUSTOMER_EMPLOYEES_ADD, employee });

        if (!employee.id) {
            return;
        }

        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.Employees.AddEmployee(
            customerId,
            employee.id,
            employee.position?.id
        );

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.employeeAdded')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const removeEmployee = (
    customerId: number,
    employeeId: number,
    reassignMainContactId?: number,
    reassignTopicsContactId?: number,
    reassignBonusContactId?: number
) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CUSTOMER_EMPLOYEES_REMOVE, employeeId, reassignMainContactId });

        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.Employees.deleteEmployee(
            customerId,
            employeeId,
            {
                reassignMainContactId,
                reassignTopicsContactId,
                reassignBonusContactId,
            }
        );

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.employeeRemoved')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const setEmployeeAsMainContact = (customerId: number, employeeId: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const employee = getState().contactsAndOrganizations.customers.customerDetail.employees.employees?.find(
            employee => employee.id === employeeId
        );
        if (!employee) {
            return;
        }

        dispatch({ type: CUSTOMER_EMPLOYEES_SET_AS_MAIN_CONTACT, employeeId });

        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.Employees.updateEmployee(
            customerId,
            employeeId,
            { isMainContact: true, positionId: employee.position?.id, tagIds: employee.tags?.map(tag => tag.id) ?? [] }
        );

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.employeeSetAsMainContact')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const updateEmployeeAsBonusContact = (customerId: number, employeeId: number, isBonusContact: boolean) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const employee = getState().contactsAndOrganizations.customers.customerDetail.employees.employees?.find(
            employee => employee.id === employeeId
        );
        if (!employee) {
            return;
        }

        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.Employees.updateEmployee(
            customerId,
            employeeId,
            { isBonusContact }
        );

        if (response.success) {
            dispatch({ type: CUSTOMER_EMPLOYEES_SET_AS_BONUS_CONTACT, employeeId, isBonusContact });
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.employeeSetAsBonusContact')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const changeEmployeePosition = (customerId: number, employeeId: number, position: Position | null) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const employee = getState().contactsAndOrganizations.customers.customerDetail.employees.employees?.find(
            employee => employee.id === employeeId
        );
        if (!employee) {
            return;
        }

        dispatch({ type: CUSTOMER_EMPLOYEES_CHANGE_POSITION, employeeId, position });

        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.Employees.updateEmployee(
            customerId,
            employeeId,
            {
                positionId: position?.id,
                tagIds: employee.tags?.map(tag => tag.id) ?? [],
                isMainContact: employee.isMainContact,
            }
        );

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.employeeChangedPosition')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const changeEmployeeTags = (customerId: number, employeeId: number, tags: Tag[]) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const employee = getState().contactsAndOrganizations.customers.customerDetail.employees.employees?.find(
            employee => employee.id === employeeId
        );
        if (!employee) {
            return;
        }

        dispatch({ type: CUSTOMER_EMPLOYEES_CHANGE_TAGS, employeeId, tags });

        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.Employees.updateEmployee(
            customerId,
            employeeId,
            {
                tagIds: tags.map(tag => tag.id),
                positionId: employee.position?.id,
                isMainContact: employee.isMainContact,
            }
        );

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.employeeTagsAssigned')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
